import { useMemo } from 'react';
import {
  AgxColumn,
  AgxRow,
  AgxBodyText,
  BlankContractProgressModel,
  AgxIconCircularGreenTick,
  AgxProgressIndicator,
} from '@urbanx/agx-ui-components';
import './BlankContractProgress.scss';

interface BlankContractProgressProps {
  blankContractProgress: BlankContractProgressModel;
}

const renderProgress = (text: string, isComplete: boolean) => (
  <AgxRow fill extraClasses="blankContractProgress">
    <AgxBodyText
      small
      extraClasses="blankContractProgressText"
      dataTestId={`bc-progress-${text.toLowerCase().replace(' ', '-')}`}
    >
      {text}
    </AgxBodyText>
    <AgxBodyText
      dataTestId={`bc-progress-${text.toLowerCase().replace(' ', '-')}-status`}
      small
      extraClasses={`blankContractProgressValue ${isComplete ? 'complete' : 'pending'}`}
    >
      {isComplete ? 'complete' : 'pending'}
    </AgxBodyText>
  </AgxRow>
);

const BlankContractProgress = ({
  blankContractProgress,
}: BlankContractProgressProps) => {
  const completedSteps = useMemo(
    () =>
      [
        blankContractProgress.titleSearchComplete &&
          blankContractProgress.showTitleSearchProgress,
        blankContractProgress.surveyPlanComplete &&
          blankContractProgress.showSurveyPlanProgress,
        blankContractProgress.sellersDisclosureComplete &&
          blankContractProgress.showSellersDisclosureProgress,
        blankContractProgress.poolSafetyComplete &&
          blankContractProgress.showPoolSafetyProgress,
        blankContractProgress.tenancyInformationComplete &&
          blankContractProgress.showTenancyInformationProgress,
        blankContractProgress.bodyCorporateComplete &&
          blankContractProgress.showBodyCorporateProgress,
        blankContractProgress.communityManagementStatementComplete &&
          blankContractProgress.showCommunityManagementStatementProgress,
        blankContractProgress.ownerBuilderComplete &&
          blankContractProgress.showOwnerBuilderProgress,
        blankContractProgress.finalisingContract,
      ].filter(step => step).length,
    [blankContractProgress]
  );
  const totalSteps = useMemo(
    () =>
      [
        blankContractProgress.showTitleSearchProgress,
        blankContractProgress.showSurveyPlanProgress,
        blankContractProgress.showSellersDisclosureProgress,
        blankContractProgress.showPoolSafetyProgress,
        blankContractProgress.showTenancyInformationProgress,
        blankContractProgress.showBodyCorporateProgress,
        blankContractProgress.showCommunityManagementStatementProgress,
        blankContractProgress.showOwnerBuilderProgress,
        1, // Finalising Contract
      ].filter(step => step).length,
    [blankContractProgress]
  );

  return (
    <AgxColumn centered extraClasses="blankContractPreparation">
      {blankContractProgress.finalisingContract ? (
        <>
          <AgxIconCircularGreenTick width={44} height={45} />
          <AgxBodyText medium centered dataTestId="age-blank-contract-ready">
            Blank Contract is ready
          </AgxBodyText>
        </>
      ) : (
        <>
          <AgxProgressIndicator
            currentSteps={completedSteps}
            totalSteps={totalSteps}
            width={60}
            height={60}
          />
          <AgxBodyText medium centered dataTestId="balnk-contract-in-progress">
            Blank Contract preparation in progress
          </AgxBodyText>
        </>
      )}
      {!blankContractProgress.finalisingContract && (
        <AgxColumn fill extraClasses="blankContractPreparationBody">
          {blankContractProgress.showTitleSearchProgress &&
            renderProgress(
              'Title Search',
              blankContractProgress.titleSearchComplete
            )}
          {blankContractProgress.showSurveyPlanProgress &&
            renderProgress(
              'Survey Plan',
              blankContractProgress.surveyPlanComplete
            )}
          {blankContractProgress.showSellersDisclosureProgress &&
            renderProgress(
              'Sellers Disclosure',
              blankContractProgress.sellersDisclosureComplete
            )}
          {blankContractProgress.showPoolSafetyProgress &&
            renderProgress(
              'Pool Safety',
              blankContractProgress.poolSafetyComplete
            )}
          {blankContractProgress.showTenancyInformationProgress &&
            renderProgress(
              'Tenancy Information',
              blankContractProgress.tenancyInformationComplete
            )}
          {blankContractProgress.showBodyCorporateProgress &&
            renderProgress(
              'Body Corporate Certificate',
              blankContractProgress.bodyCorporateComplete
            )}
          {blankContractProgress.showCommunityManagementStatementProgress &&
            renderProgress(
              'Community Management Statement',
              blankContractProgress.communityManagementStatementComplete
            )}
          {blankContractProgress.showOwnerBuilderProgress &&
            renderProgress(
              'Owner Builder Declaration',
              blankContractProgress.ownerBuilderComplete
            )}
          {renderProgress(
            'Finalising Contract',
            blankContractProgress.finalisingContract
          )}
        </AgxColumn>
      )}
    </AgxColumn>
  );
};

export default BlankContractProgress;
